const CLOUDINARY_BASE = "https://img.peerspace.com/image/upload";

type BuildListingThumbnailUrlArgs = {
  externalId?: string;
  size: number;
};

type BuildAvatarUrlArgs = {
  avatarId?: string;
  size: number;
};

type SpaceThumbnailImage = {
  external_id?: string;
  urlSmall?: string;
};

type PickSpaceThumbnailUrlArgs = {
  images: SpaceThumbnailImage[] | undefined;
  size: number;
};

type ListingThumbnailImage = {
  external_id?: string;
  thumbnail_url?: string;
  url?: string;
};

type PickListingThumbnailUrlArgs = {
  images: ListingThumbnailImage[] | undefined;
  size: number;
};

/**
 * Builds a square Cloudinary listing thumbnail URL from a stored `external_id`,
 * mirroring the transform the legacy `ListingImage` component uses. Returns
 * `undefined` when no id is available so callers can fall back to an icon.
 */
export const buildListingThumbnailUrl = ({
  externalId,
  size,
}: BuildListingThumbnailUrlArgs): string | undefined => {
  if (!externalId) return undefined;
  return `${CLOUDINARY_BASE}/c_crop,g_custom/g_auto,c_fill,q_auto,f_auto,fl_progressive:steep,dpr_2.0,w_${size},h_${size}/${externalId}`;
};

/**
 * Builds a square Cloudinary avatar URL from a stored `avatar_id`, mirroring the
 * transform the legacy `Avatar` component uses. Returns `undefined` when no id
 * is available.
 */
export const buildAvatarUrl = ({
  avatarId,
  size,
}: BuildAvatarUrlArgs): string | undefined => {
  if (!avatarId) return undefined;
  return `${CLOUDINARY_BASE}/c_crop,g_custom/f_auto,fl_progressive/c_scale,dpr_auto,h_${size},w_${size}/${avatarId}`;
};

/**
 * Resolves a square thumbnail URL for a space from its image list. Prefers a
 * Cloudinary transform off the first image's `external_id`, then falls back to
 * a stored `urlSmall`. Returns `undefined` when no usable image is available so
 * callers can render an icon instead.
 */
export const pickSpaceThumbnailUrl = ({
  images,
  size,
}: PickSpaceThumbnailUrlArgs): string | undefined => {
  const externalId = images?.[0]?.external_id;
  if (externalId) return buildListingThumbnailUrl({ externalId, size });

  for (const image of images ?? []) {
    if (image.urlSmall) return image.urlSmall;
  }
  return undefined;
};

/**
 * Resolves a square thumbnail URL for a listing from its image list. Prefers a
 * Cloudinary transform off a legacy `external_id` (v1), then falls back to the
 * ready-to-use `thumbnail_url`/`url` that `spaces-v2` returns. Returns
 * `undefined` when no usable image is available so callers can render an icon.
 */
export const pickListingThumbnailUrl = ({
  images,
  size,
}: PickListingThumbnailUrlArgs): string | undefined => {
  const image = images?.[0];
  if (image?.external_id) {
    return buildListingThumbnailUrl({ externalId: image.external_id, size });
  }
  return image?.thumbnail_url ?? image?.url;
};
